import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, X } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Bookings() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [assign, setAssign] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get("/bookings").then(({ data }) => setItems(data)).catch((e) => setError(errMsg(e)));

  useEffect(() => {
    load();
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
    api.get("/chauffeurs").then(({ data }) => setChauffeurs(data)).catch(() => {});
  }, []);

  const convert = async (b) => {
    setBusy(b.id);
    setError("");
    const a = assign[b.id] || {};
    const estimate = Number(b.estimated_total || 0);
    try {
      const { data } = await api.post(`/bookings/${b.id}/convert`, {
        vehicle_id: a.vehicle_id || b.vehicle_id || null,
        chauffeur_id: a.chauffeur_id || null,
        total_charge: Number(a.total_charge ?? estimate ?? 0),
      });
      navigate(`/app/manifests/${data.id}`);
    } catch (e) {
      setError(errMsg(e));
      setBusy("");
    }
  };

  const pending = items.filter((b) => b.status === "pending");
  const handled = items.filter((b) => b.status !== "pending");

  return (
    <div data-testid="bookings-page" className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Inbound</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Booking requests</h1>
        <p className="mt-2 text-sm text-slate-400">
          Requests from your public booking page and client portal. Price them, assign a vehicle and chauffeur,
          and convert straight into a manifest.
        </p>
      </div>

      {error && <p data-testid="bookings-error" className="text-sm text-red-400">{error}</p>}

      {pending.length === 0 && (
        <p data-testid="bookings-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-8 text-center text-sm text-slate-500">
          No pending requests.
        </p>
      )}

      <div className="space-y-4">
        {pending.map((b) => {
          const a = assign[b.id] || {};
          const estimate = Number(b.estimated_total || 0);
          const set = (k, v) => setAssign({ ...assign, [b.id]: { ...a, [k]: v } });
          return (
            <div key={b.id} data-testid={`booking-card-${b.id}`} className="rounded-xl border border-ink-600 bg-ink-800 p-6">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <p className="text-base font-medium text-slate-100">{b.customer_name}</p>
                  <p className="mt-1 text-xs text-slate-400">{b.customer_email} · {b.customer_phone}</p>
                  <div className="mt-4 space-y-1.5 text-sm text-slate-300">
                    <p><span className="text-slate-500">Pickup:</span> {b.pickup?.address || "—"}</p>
                    <p><span className="text-slate-500">Drop-off:</span> {b.dropoff?.address || "—"}</p>
                    <p><span className="text-slate-500">When:</span> {b.trip_date} {b.pickup_time}</p>
                    <p><span className="text-slate-500">Passengers:</span> {b.passengers} · {b.vehicle_pref || "no preference"}</p>
                    {[b.airline, b.flight_number, b.terminal].filter(Boolean).length > 0 && (
                      <p><span className="text-slate-500">Flight:</span> {[b.airline, b.flight_number, b.terminal].filter(Boolean).join(" · ")}</p>
                    )}
                    {b.notes && <p className="text-xs text-slate-400">“{b.notes}”</p>}
                  </div>
                  {estimate > 0 && (
                    <div data-testid={`booking-estimate-${b.id}`} className="mt-4 rounded-lg border border-gold/30 bg-gold/5 p-3">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-gold">
                        Priced for the passenger
                      </p>
                      <p className="mt-1 text-sm text-slate-200">
                        {money(estimate)}
                        {Number(b.deposit_due || 0) > 0
                          ? ` · ${money(b.deposit_due)} due at reservation`
                          : " · nothing due until after the trip"}
                        {Number(b.amount_paid || 0) > 0 ? ` · ${money(b.amount_paid)} already paid` : ""}
                      </p>
                      {b.payment_terms && (
                        <p className="mt-1 font-mono text-[10px] text-slate-500">{b.payment_terms}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <select
                    data-testid={`booking-vehicle-${b.id}`}
                    value={a.vehicle_id ?? b.vehicle_id ?? ""}
                    onChange={(e) => {
                      const v = vehicles.find((x) => x.id === e.target.value);
                      setAssign({ ...assign, [b.id]: { ...a, vehicle_id: e.target.value, total_charge: a.total_charge ?? (estimate || (v ? v.base_rate : "")) } });
                    }}
                    className="h-11 w-full rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
                  >
                    <option value="">Select vehicle</option>
                    {vehicles.map((v) => <option key={v.id} value={v.id}>{`${v.name} · ${money(v.base_rate)}`}</option>)}
                  </select>
                  <select
                    data-testid={`booking-chauffeur-${b.id}`}
                    value={a.chauffeur_id || ""}
                    onChange={(e) => set("chauffeur_id", e.target.value)}
                    className="h-11 w-full rounded-md border border-ink-600 bg-ink-900 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold"
                  >
                    <option value="">Select chauffeur</option>
                    {chauffeurs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <Input
                    data-testid={`booking-total-${b.id}`}
                    type="number"
                    step="0.01"
                    placeholder="Total charge"
                    value={a.total_charge ?? (estimate || "")}
                    onChange={(e) => set("total_charge", e.target.value)}
                    className="h-11 border-ink-600 bg-ink-900 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold"
                  />
                  <div className="flex gap-2">
                    <Button data-testid={`booking-convert-${b.id}`} disabled={busy === b.id} onClick={() => convert(b)} className="h-11 flex-1 bg-gold text-ink-900 hover:bg-gold-light">
                      {busy === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Check className="mr-2 h-4 w-4" /> Create manifest</>)}
                    </Button>
                    <Button
                      data-testid={`booking-decline-${b.id}`}
                      variant="outline"
                      onClick={async () => { await api.post(`/bookings/${b.id}/decline`); load(); }}
                      className="h-11 border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {handled.length > 0 && (
        <div className="rounded-xl border border-ink-600 bg-ink-800 p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Handled requests</p>
          <div className="mt-4 divide-y divide-ink-600">
            {handled.map((b) => (
              b.manifest_id ? (
                <Link key={b.id} to={`/app/manifests/${b.manifest_id}`}
                  data-testid={`handled-booking-${b.id}`}
                  className="flex items-center justify-between gap-4 py-3 text-sm transition-colors hover:text-gold">
                  <span className="truncate text-slate-300">{b.customer_name} · {b.trip_date} {b.pickup_time}</span>
                  <span className="font-mono text-xs text-gold">open trip →</span>
                </Link>
              ) : (
                <div key={b.id} data-testid={`handled-booking-${b.id}`}
                  className="flex items-center justify-between gap-4 py-3 text-sm">
                  <span className="truncate text-slate-300">{b.customer_name} · {b.trip_date} {b.pickup_time}</span>
                  <span className="font-mono text-xs text-slate-500">{b.status}</span>
                </div>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
